/* eslint-disable promise/prefer-await-to-then */
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { Bell, Calendar, LayoutDashboard, List, ListChecks, ShieldAlert, Workflow } from 'lucide-react';
import { ReactElement } from 'react';
import { Navigate, NonIndexRouteObject, Outlet, RouteObject } from 'react-router-dom';

import { AppRoute } from '@/constants/app-routes.ts';

import { EnrichedCustomer } from '@/models/EnrichedPrismicDocuments.ts';
import { UserEntitlement, UserGroup, type UserGroupOrEntitlement } from '@/models/User.ts';

import CalendarViewPage from '@/pages/CalendarViewPage.tsx';
import CareviewPage from '@/pages/CareviewPage.tsx';
import DemoLoginPage from '@/pages/DemoLoginPage.tsx';
import LoginPage from '@/pages/LoginPage.tsx';
import RoomViewPage from '@/pages/RoomViewPage.tsx';
import TaskViewPage from '@/pages/TaskViewPage.tsx';

import { isEternoDomain } from '@/utils/envUtils.ts';

import { OfficeAppShell } from './components/office-app-shell.tsx';

interface EnrichedRoute extends NonIndexRouteObject {
    name?: string;
    key?: string;
    icon?: ReactElement;
}

export const getAuthenticatedRoutes = (
    customer: EnrichedCustomer,
    isUserInGroup: (groups: UserGroupOrEntitlement | UserGroupOrEntitlement[]) => boolean
): RouteObject[] => {
    const routes: EnrichedRoute[] = [
        {
            path: AppRoute.ROOT,
            element: <RoomViewPage />,
            name: 'Room view',
            key: 'room-view',
            icon: <Bell />,
        },
        {
            path: AppRoute.LIST_VIEW,
            lazy: () => import('@/pages/ListViewPage.tsx').then((m) => ({ Component: m.default })),
            name: 'List View',
            key: 'list-view',
            icon: <List />,
        },
        {
            path: AppRoute.TASKS,
            element: <TaskViewPage />,
            name: 'Task View',
            key: 'task-view',
            icon: <ListChecks />,
        },
        {
            path: AppRoute.PLAYGROUND,
            lazy: () => import('@/pages/PlaygroundPage.tsx').then((m) => ({ Component: m.default })),
        },
        {
            path: AppRoute.CHAT,
            lazy: () => import('@/pages/ChatPage.tsx').then((m) => ({ Component: m.default })),
        },
        {
            path: AppRoute.STATIC_QR_CODE,
            lazy: () => import('@/pages/static-qr-code-page.tsx').then((m) => ({ Component: m.StaticQrCodePage })),
        },
        {
            path: AppRoute.HANDBOOK,
            lazy: () => import('@/pages/handbook-page.tsx').then((m) => ({ Component: m.HandbookPage })),
        },
        {
            path: AppRoute.CALENDAR_VIEW,
            element: <CalendarViewPage />,
            name: 'Calendar View',
            key: 'calendar-view',
            icon: <Calendar />,
        },
    ];

    const isEngineerOrQa = isUserInGroup([
        UserGroup.DEV_INTERNAL_ENGINEERS,
        UserGroup.PROD_INTERNAL_ENGINEERS,
        UserGroup.DEV_INTERNAL_QA,
        UserGroup.PROD_INTERNAL_QA,
    ]);
    const isProductManager = isUserInGroup(UserGroup.INTERNAL_PRODUCT_MANAGER);
    const isHubManager = isUserInGroup(UserGroup.INTERNAL_HUB_MANAGER);

    if (isEngineerOrQa) {
        routes.push({
            path: AppRoute.MAPPINGS,
            lazy: () => import('@/pages/admin/mappings-page.tsx').then((m) => ({ Component: m.MappingsPage })),
            name: 'Mappings',
            key: 'mappings',
            icon: <Workflow />,
        });
    }

    if (isUserInGroup(UserGroup.FEATURE_CAREVIEW)) {
        routes.push({
            path: AppRoute.CAREVIEW,
            element: <CareviewPage />,
            name: 'CareView',
            key: 'careview',
            icon: <LayoutDashboard />,
        });
    }

    if (isUserInGroup(UserEntitlement.ENTITLEMENT_BOOKING_SHIELD)) {
        routes.push({
            path: AppRoute.BOOKING_SHIELD,
            lazy: () => import('@/pages/booking-shield-page.tsx').then((m) => ({ Component: m.BookingShieldPage })),
            name: 'BookingShield',
            key: 'booking-shield',
            icon: <ShieldAlert />,
        });
    }

    if (isUserInGroup(UserEntitlement.ENTITLEMENT_DC_USER_MANAGEMENT)) {
        routes.push({
            path: AppRoute.DC_USER_MANAGEMENT,
            lazy: () =>
                import('@/pages/dc-user-management-page.tsx').then((m) => ({ Component: m.DcUserManagementPage })),
        });
    }

    if (isEngineerOrQa || isProductManager) {
        const addedRoutes: EnrichedRoute[] = [
            {
                path: AppRoute.UNREGISTERED_USER_DOCUMENTS,
                lazy: () => import('@/pages/UnregisteredUserDocumentsPage.tsx').then((m) => ({ Component: m.default })),
            },
            {
                path: AppRoute.CUSTOMER_MANAGEMENT,
                lazy: () =>
                    import('@/pages/admin/customer-management-page.tsx').then((m) => ({
                        Component: m.CustomerManagementPage,
                    })),
            },
            {
                path: AppRoute.DATA_CONFIGS,
                lazy: () =>
                    import('@/pages/admin/data-configs-page.tsx').then((m) => ({ Component: m.DataConfigsPage })),
            },
            {
                path: AppRoute.USER_MANAGEMENT,
                lazy: () =>
                    import('@/pages/admin/user-management-page.tsx').then((m) => ({ Component: m.UserManagementPage })),
            },
        ];

        routes.push(...addedRoutes);
    }

    if (isEngineerOrQa || isProductManager || isHubManager) {
        routes.push({
            path: AppRoute.CHAINS,
            lazy: () => import('@/pages/admin/chains-config-page.tsx').then((m) => ({ Component: m.ChainsConfigPage })),
            name: 'Chains Configs',
            key: 'chains-configs',
            icon: <Workflow />,
        });
    }

    routes.push({
        path: AppRoute.FALLBACK,
        element: <Navigate to={AppRoute.ROOT} replace />,
    });

    return [
        {
            path: AppRoute.ROOT,
            element: (
                <ModalsProvider>
                    <Notifications />
                    <OfficeAppShell>
                        <Outlet />
                    </OfficeAppShell>
                </ModalsProvider>
            ),
            children: routes,
        },
    ];
};

export const getUnauthenticatedRoutes = (): RouteObject[] => {
    const routes: EnrichedRoute[] = [
        {
            path: AppRoute.ROOT,
            element: <Navigate to={AppRoute.LOGIN} replace />,
        },
        {
            path: AppRoute.LOGIN,
            element: <LoginPage />,
        },
        {
            path: AppRoute.MAGIC_LINK,
            lazy: () => import('@/pages/MagicLinkPage.tsx').then((m) => ({ Component: m.default })),
        },
    ];

    if (!isEternoDomain()) {
        routes.push({
            path: AppRoute.DEMO_LOGIN,
            element: <DemoLoginPage />,
        });
    }

    routes.push({
        path: AppRoute.FALLBACK,
        element: <Navigate to={AppRoute.LOGIN} replace />,
    });

    return [
        {
            path: AppRoute.ROOT,
            element: (
                <>
                    <Notifications />
                    <Outlet />
                </>
            ),
            children: routes,
        },
    ];
};
